import { useCallback, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { AddIcon } from '@chakra-ui/icons';
import { HStack } from '@chakra-ui/react';

import { Button } from '@/components/v4';
import AGGridMain from 'components/tables/AGGrid/AGGridMain';
import useColumnState from 'hooks/useColumnState';

import { SBC_MODES } from './constants';
import useGetSBCsByLocation from './queries/useGetSBCsByLocation';
import SBCForm from './SBCForm';
import { getSBCTableColumns } from './sbcTableConfig';

const SBCManager = ({ locationId }) => {
    const gridRef = useRef();
    const { darkMode } = useSelector((state) => state.settings);

    const [mode, setMode] = useState(SBC_MODES.LIST);
    const [selectedSBCId, setSelectedSBCId] = useState(null);
    const [tableData, setTableData] = useState({ columns: [], data: [] });

    const resetToList = useCallback(() => {
        setMode(SBC_MODES.LIST);
        setSelectedSBCId(null);
    }, []);

    const handleAdd = useCallback(() => {
        setMode(SBC_MODES.ADD);
    }, []);

    const handleEdit = useCallback((sbcData) => {
        setSelectedSBCId(sbcData.id);
        setMode(SBC_MODES.EDIT);
    }, []);

    const baseColumns = useMemo(
        () => getSBCTableColumns({ darkMode, onEditSBC: handleEdit }),
        [darkMode, handleEdit],
    );

    const { columns, onColumnVisible } = useColumnState(baseColumns);

    const tableDataWithColumns = useMemo(
        () => ({ columns, data: tableData.data }),
        [columns, tableData.data],
    );

    const { refetch, isRefetching, isFetching } = useGetSBCsByLocation(
        locationId,
        setTableData,
        gridRef,
        baseColumns,
    );

    const isFetchingData = isRefetching || isFetching;

    if (mode === SBC_MODES.ADD) {
        return (
            <SBCForm
                mode="Add"
                locationId={locationId}
                onDone={resetToList}
                onCancel={resetToList}
            />
        );
    }

    if (mode === SBC_MODES.EDIT) {
        return (
            <SBCForm
                mode="Edit"
                locationId={locationId}
                sbcId={selectedSBCId}
                onDone={resetToList}
                onCancel={resetToList}
            />
        );
    }

    return (
        <AGGridMain
            gridRef={gridRef}
            tableData={tableDataWithColumns}
            isLoading={isFetchingData}
            titleName="SBC"
            refetch={refetch}
            onColumnVisible={onColumnVisible}
            overlayNoRowsTemplate="No SBCs Found"
            extraComponents={
                <HStack pl={5} spacing={2}>
                    <Button
                        leftIcon={<AddIcon />}
                        onClick={handleAdd}
                        size="sm"
                        padding="6px 12px">
                        Add SBC
                    </Button>
                </HStack>
            }
        />
    );
};

export default SBCManager;
